// Style imports, with definition order:
// vendor.css (linked from index.html), imported library styles, app.css, (CSS modules).
import "@appnest/masonry-layout"
import "ember-power-select/vendor/ember-power-select-material.css"
import "/app/styles/app.css"

import { inject as dubAnalytics } from "@dub/analytics"
import Application from "@ember/application"
import setupInspector from "@embroider/legacy-inspector-support/ember-source-3.28"
import compatModules from "@embroider/virtual/compat-modules"
import "@glint/environment-ember-loose"
import * as Sentry from "@sentry/browser"
import Ember from "ember"
import loadInitializers from "ember-load-initializers"
import { registerDateLibrary } from "ember-power-calendar"
import DateUtils from "ember-power-calendar-moment"
import Resolver from "ember-resolver"
import "reflect-metadata"
import { on } from "rsvp"
import config from "./config/environment"
import { analytics, handleError } from "./error-handling"
import "./reopenings/route"

if (config.environment === "production") {
  analytics.load(
    {
      writeKey: "vll1uqsjn1",
      cdnURL: "https://segcdn.commoncurriculum.com",
    },
    {
      integrations: {
        All: true,
        // We'll load fullstory ourselves
        FullStory: false,
      },
      obfuscate: true,
    }
  )
}

// Initialize Dub.co analytics for affiliate click tracking.
// This detects Dub link clicks (via the dub_id query param) and stores
// the click ID in a first-party cookie for deferred lead conversion tracking.
// The publishable key below is public and safe to commit.
// Script and API calls are routed through our self-hosted proxy to avoid
// ad-blocker interference.
if (config.environment === "production" && typeof document !== "undefined") {
  dubAnalytics({
    publishableKey: "dub_pk_TY6CozdiF1qhN5NhIMD6vugQ",
    apiHost: "https://dub.commonplanner.com/_proxy/dub",
    queryParams: ["via", "ref"],
    domainsConfig: {
      refer: "cmpln.com",
      outbound: ["app.cal.com", "cal.com", "l.commonplanner.com"],
    },
    scriptProps: {
      src: "https://dub.commonplanner.com/_proxy/dub/script.js",
    },
  })
}

console.log(`Client Version: ${config.APP.version}`)
if (window.location) {
  let href = window.location.pathname
  let newHref = href.replace(/[/]{2,}/, "/")
  if (href !== newHref) {
    console.log("Replacing URL as it has double slashes in it.")
    window.history.replaceState({ path: newHref }, "", newHref)
  }
}

// Make sure the default title tag is removed once we build the app
if (typeof document !== "undefined") {
  document.getElementById("title-before-boot")?.remove()
}

if (window === undefined) {
  // eslint-disable-next-line no-global-assign
  document = document || {}
}

if (config.environment === "production") {
  let version = config.CLIENT_VERSION.replace("Cc.", "")
  Sentry.init({
    dsn: config.SENTRY_DSN_PUBLIC,
    release: version,
    environment: `${config.environment}_ember`,
    integrations: [new Sentry.Integrations.Breadcrumbs({ xhr: false })],
    beforeSend(event, hint) {
      return event
    },
  })
  Sentry.setTag("clientVersion", config.CLIENT_VERSION)
}

if (config.environment !== "test") {
  Ember.onerror = function (error) {
    // I'm not sure why we're not able to catch this error other places.
    // Since this is a problem with what appears to be a stale connection
    // this should fix the issue
    if (typeof error === "object" && error !== null && error["reason"] === "unmatched topic") {
      window.location.reload()
    } else {
      handleError(error)
    }
  }
}

on("error", function (reason) {
  console.log("RSVP ERROR", reason)
  // let id = ObjectId.create()
  // postError(reason, id)
  // displayError(id)
})

class App extends Application {
  modulePrefix = config.modulePrefix
  podModulePrefix = config.podModulePrefix
  Resolver = Resolver.withModules(compatModules)
  inspector = setupInspector(this)
}

// @ts-ignore
loadInitializers(App, config.modulePrefix, compatModules)

// See https://github.com/cibernox/ember-power-calendar-moment setup instructions.
registerDateLibrary(DateUtils)

// Prevent zooming in on text inputs on iOS, by adding maximum-scale=1.0 *only* there.
// We don't want to add it all the time because it prevents pinch-zooming on Android
// and prevents the site from zooming with the rest of the page when embedded in an iFrame.
// See https://lukeplant.me.uk/blog/posts/you-can-stop-using-user-scalable-no-and-maximum-scale-1-in-viewport-meta-tags-now/.

// Code based on https://stackoverflow.com/questions/2989263/disable-auto-zoom-in-input-text-tag-safari-on-iphone
// and https://stackoverflow.com/questions/9038625/detect-if-device-is-ios/9039885#9039885
if (typeof navigator !== "undefined" && typeof document !== "undefined") {
  const isIOS =
    ["iPad Simulator", "iPhone Simulator", "iPod Simulator", "iPad", "iPhone", "iPod"].includes(
      navigator.platform
    ) ||
    // iPad on iOS 13 detection
    (navigator.userAgent.includes("Mac") && "ontouchend" in document)
  if (isIOS) {
    const el = document.querySelector("meta[name=viewport]")

    if (el !== null) {
      let content = el.getAttribute("content") || ""
      let re = /maximum-scale=[0-9.]+/g

      if (re.test(content)) {
        content = content.replace(re, "maximum-scale=1.0")
      } else {
        content = [content, "maximum-scale=1.0"].join(", ")
      }

      el.setAttribute("content", content)
    }
  }
}

export default App
